















































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientProjectOrganisation } from "@/data/models/ClientAdministrationRow";
import { ClientSetupProjectOrganisation, ProjectOrganisationApp } from "@/data/models/ClientSetup";
import { setClientProjectOrganisation } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  PROJECT_ORGANISATION_QUESTIONS,
  canSaveProjectOrganisation,
  clientProjectOrganisationErrorKey,
  copyProjectOrganisation,
  mayBeNowhere,
  projectOrganisationDiffers,
  projectSettingsOptionText,
  questionApps,
} from "@/utils/ClientProjectOrganisation";

/** What `show()` resolves with: the saved Projektorganisation, CHANGED_MEANWHILE (the caller reloads), or null. */
export type ProjectOrganisationDialogResult = ClientProjectOrganisation | "CHANGED_MEANWHILE" | null;

const EMPTY_ANSWERS: ClientSetupProjectOrganisation = {
  newProjectsIn: [],
  editProjectsIn: [],
  finaliseProjectsIn: [],
  newAppointmentsIn: [],
};

const EMPTY_ORGANISATION: ClientProjectOrganisation = {
  value: EMPTY_ANSWERS,
  stored: false,
  offered: [],
  hasProjects: false,
  followsMasterClientId: null,
};

/**
 * Edits a client's Projektorganisation (2026-10-29): where projects are created, edited and finished and
 * where appointments are created, one checkbox per app the products offer (plus one a question already has
 * that they no longer offer, which can only be taken away). The answers are sent with the ones loaded, so
 * IRES Cloud can refuse a change made meanwhile.
 *
 * `show()` resolves with the saved Projektorganisation, `CHANGED_MEANWHILE` when IRES Cloud refused because
 * the client changed since (the caller reloads), or null when closed without saving.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientProjectOrganisationDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  questions = PROJECT_ORGANISATION_QUESTIONS;
  busy = false;
  clientId = "";
  isMaster = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientProjectOrganisation = { ...EMPTY_ORGANISATION };
  form: ClientSetupProjectOrganisation = copyProjectOrganisation(EMPTY_ANSWERS);

  private resolve: ((result: ProjectOrganisationDialogResult) => void) | null = null;

  get hasChanged(): boolean {
    return projectOrganisationDiffers(this.stored.value, this.form);
  }

  get canSave(): boolean {
    return canSaveProjectOrganisation(this.stored.value, this.form);
  }

  appsOf(question: keyof ClientSetupProjectOrganisation): ProjectOrganisationApp[] {
    return questionApps(this.stored, question);
  }

  appText(app: ProjectOrganisationApp): string {
    return projectSettingsOptionText(app, (key) => this.$t(key).toString());
  }

  nowhereAllowed(question: keyof ClientSetupProjectOrganisation): boolean {
    return mayBeNowhere(question);
  }

  /**
   * @param clientId - the client.
   * @param stored - its Projektorganisation as the detail loaded it.
   * @param isMaster - whether it is a MASTER-Mandant, whose answers its sharing members use too.
   */
  show(clientId: string, stored: ClientProjectOrganisation, isMaster: boolean): Promise<ProjectOrganisationDialogResult> {
    this.clientId = clientId;
    this.isMaster = isMaster;
    this.stored = { ...stored, value: copyProjectOrganisation(stored.value) };
    this.form = copyProjectOrganisation(stored.value);
    this.baseDialog.show({
      title: this.$t("admin.clients.projectOrganisation.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await setClientProjectOrganisation(this.clientId, copyProjectOrganisation(this.stored.value), copyProjectOrganisation(this.form));
      SnackbarModule.showSuccess({ key: "admin.clients.projectOrganisation.saved" });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const key = clientProjectOrganisationErrorKey(error);
      SnackbarModule.showError({ key });
      // Someone changed the client meanwhile: close, so the caller reloads from the new values.
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: ProjectOrganisationDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: ProjectOrganisationDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
